import type {
  FieldError,
  GoodsIssueDetail,
  GoodsIssueSource,
  GoodsIssueStatus,
} from '../types';
import {
  candidateLabel,
  type EditCheck,
  type EditRow,
  isDirty,
  type OrderedSku,
  orderedFromLines,
  type RowErrors,
  type RowField,
  rowsFromLines,
  type SourceKnowledge,
  sourceKey,
  stockGone,
  UNAVAILABLE,
} from './pickingEdit';

/**
 * Pure state of the bulk picking editor ("Edit picking" for the pickings
 * selected on the Picking list): one draft per goods issue — what was loaded,
 * what the user changed, which candidates its SKUs can be picked from and how
 * its last save ended — plus the helpers that decide what a save sends, which
 * picking is next and the tally. No React here; useBulkPicking holds
 * the state and BulkPickingModal renders it.
 */

/** How one picking of the batch stands. */
export type ItemState = 'pending' | 'saved' | 'ready' | 'stale' | 'failed';

/** What the list is told when the modal closes. */
export interface BulkPickingOutcome {
  id: string;
  state: ItemState;
}

export const STATE_LABEL: Record<ItemState, string> = {
  pending: 'Belum diproses',
  saved: 'Tersimpan (Picked)',
  ready: 'Siap diambil',
  stale: 'Berubah sejak dimuat',
  failed: 'Gagal',
};

/** Done: leaves the list's selection. */
export const isSettled = (s: ItemState): boolean =>
  s === 'saved' || s === 'ready';

/** One SKU's candidate stock rows: loaded, or why not. */
export type SourceSlot =
  | { status: 'ready'; data: GoodsIssueSource[] }
  | { status: 'error'; message: string };

export interface ItemDraft {
  state: ItemState;
  /** Why it is stale / failed (the server's words). */
  message: string | null;
  /** null until loaded (or while it is being reloaded). */
  detail: GoodsIssueDetail | null;
  loadError: string | null;
  ordered: OrderedSku[];
  /** The rows as loaded / last saved — "dirty" is measured against these. */
  initial: EditRow[];
  rows: EditRow[];
  /** The picker field. */
  picker: string;
  initialPicker: string;
  /** Candidates per SKU; a SKU without a slot is being loaded. */
  sources: Record<string, SourceSlot>;
  /** The last 422, by row key. */
  serverErrors: Map<number, RowErrors>;
  formErrors: string[];
  pickerError: string | null;
}

export const emptyDraft = (): ItemDraft => ({
  state: 'pending',
  message: null,
  detail: null,
  loadError: null,
  ordered: [],
  initial: [],
  rows: [],
  picker: '',
  initialPicker: '',
  sources: {},
  serverErrors: new Map(),
  formErrors: [],
  pickerError: null,
});

/** A freshly loaded detail: draft rows / picker start from it, state pending. */
export const draftFromDetail = (
  prev: ItemDraft,
  detail: GoodsIssueDetail
): ItemDraft => {
  const rows = rowsFromLines(detail.lines);
  const picker = detail.goodsIssue.pickerName ?? '';
  return {
    ...prev,
    state: 'pending',
    message: null,
    detail,
    loadError: null,
    ordered: orderedFromLines(detail.lines),
    initial: rows,
    rows,
    picker,
    initialPicker: picker,
    serverErrors: new Map(),
    formErrors: [],
    pickerError: null,
  };
};

/**
 * What the server answered to a write that went through: its version, lines
 * and picker become the baseline. The user's picker field is kept, so a
 * following step that failed can be retried with the same input.
 */
export const withSavedDetail = (
  d: ItemDraft,
  detail: GoodsIssueDetail
): ItemDraft => {
  const rows = rowsFromLines(detail.lines);
  return {
    ...d,
    detail,
    ordered: orderedFromLines(detail.lines),
    initial: rows,
    rows,
    initialPicker: detail.goodsIssue.pickerName ?? '',
    serverErrors: new Map(),
    formErrors: [],
  };
};

export const pickerChanged = (d: ItemDraft): boolean =>
  d.picker.trim() !== d.initialPicker.trim();

/** The user changed something that is not saved yet. */
export const isItemDirty = (d: ItemDraft): boolean =>
  !!d.detail && (isDirty(d.initial, d.rows) || pickerChanged(d));

/* ------------------------------ save planning ----------------------------- */

export type SaveKind = 'save' | 'ready';

export interface SavePlan {
  /** PUT /lines first. */
  putLines: boolean;
  /** PATCH /picker (save only). */
  setPicker: boolean;
  /** POST /ready-to-pickup (ready only). */
  ready: boolean;
  /** Nothing to send (save only: a Picked list, same rows, same picker). */
  nothing: boolean;
}

/**
 * What a save sends. "Simpan saja" rewrites the lines when they changed or
 * when the list is still Not picked (saving confirms the pick), then sets the
 * picker if it changed. "Simpan & siap diambil" rewrites the lines only when
 * they changed — ready-to-pickup fills picked = reserved itself.
 */
export const savePlan = (
  kind: SaveKind,
  d: ItemDraft,
  status: GoodsIssueStatus
): SavePlan => {
  const dirtyRows = isDirty(d.initial, d.rows);
  if (kind === 'ready') {
    return {
      putLines: dirtyRows,
      setPicker: false,
      ready: true,
      nothing: false,
    };
  }
  const putLines = dirtyRows || status === 'RESERVED';
  const setPicker = pickerChanged(d);
  return {
    putLines,
    setPicker,
    ready: false,
    nothing: !putLines && !setPicker,
  };
};

export type SaveErrorKind = 'stale' | 'insufficient' | 'validation' | 'failed';

/**
 * What a refused save means. The goods issue moved under us (409 — the
 * version token, or no longer Not picked / Picked — or 404): the draft was not
 * saved and the item has to be reloaded. 422s stay on the item.
 */
export const classifySaveError = (e: {
  status: number | null;
  code: string | null;
}): SaveErrorKind => {
  if (
    e.code === 'GOODS_ISSUE_STALE' ||
    e.code === 'INVALID_STATUS_TRANSITION' ||
    e.status === 409 ||
    e.status === 404
  ) {
    return 'stale';
  }
  if (e.code === 'INSUFFICIENT_STOCK') return 'insufficient';
  if (e.code === 'VALIDATION' || e.status === 422) return 'validation';
  return 'failed';
};

/** A 422's picker message apart from the rest (which names lines / the form). */
export const splitPickerError = (
  errors: readonly FieldError[]
): { picker: string | null; rest: FieldError[] } => {
  const picker = errors.find((e) => e.field === 'pickerName');
  return {
    picker: picker?.message ?? null,
    rest: errors.filter((e) => e.field !== 'pickerName'),
  };
};

/* ------------------------------- candidates ------------------------------- */

const slotData = (d: ItemDraft, sku: string): GoodsIssueSource[] | null => {
  const slot = d.sources[sku];
  return slot?.status === 'ready' ? slot.data : null;
};

/**
 * What checkRows knows about the stock: every candidate loaded so far and, for
 * each SKU whose list is in, which stock rows each storage holds (a row's
 * storage that holds none → an empty list, so a vanished row is flagged).
 */
export const knowledgeOf = (d: ItemDraft): SourceKnowledge => {
  const candidates = new Map<string, GoodsIssueSource>();
  const lists = new Map<string, Set<string>>();
  Object.keys(d.sources).forEach((sku) => {
    const data = slotData(d, sku);
    if (!data) return;
    data.forEach((c) => {
      candidates.set(c.stockID, c);
      const key = sourceKey(sku, c.storageID);
      const set = lists.get(key) ?? new Set<string>();
      set.add(c.stockID);
      lists.set(key, set);
    });
  });
  d.rows.forEach((r) => {
    if (!r.sku || !r.storageID || !slotData(d, r.sku)) return;
    const key = sourceKey(r.sku, r.storageID);
    if (!lists.has(key)) lists.set(key, new Set());
  });
  return { candidates, lists };
};

export interface CandidateOption {
  value: string;
  label: string;
}

/** The SKU's candidates a row may still pick: not used by another row of it. */
export const optionsFor = (d: ItemDraft, row: EditRow): CandidateOption[] => {
  const data = slotData(d, row.sku);
  if (!data) return [];
  const used = new Set(
    d.rows
      .filter((r) => r !== row && r.sku === row.sku && r.stockID)
      .map((r) => r.stockID)
  );
  return data
    .filter((c) => !used.has(c.stockID))
    .map((c) => ({ value: c.stockID, label: candidateLabel(c, c.available) }));
};

/** What the row's picker shows: its candidate, else the line it started from. */
export const rowLabel = (
  row: EditRow,
  known: SourceKnowledge
): string | undefined => {
  if (!row.stockID) return undefined;
  const cand = known.candidates.get(row.stockID);
  const seed = row.seed && row.seed.stockID === row.stockID ? row.seed : null;
  const base = cand
    ? candidateLabel(cand, cand.available)
    : seed
      ? candidateLabel(seed)
      : row.stockID;
  return stockGone(known, row) ? base + UNAVAILABLE : base;
};

/* --------------------------------- layout --------------------------------- */

export interface SkuGroup {
  sku: string;
  items: { row: EditRow; index: number }[];
}

/**
 * Rows grouped by SKU, in the order of the ordered SKUs (a row of a SKU that
 * is not among them goes last). `index` is the row's place in `rows` — the
 * `lines[i]` a 422 names.
 */
export const groupRows = (
  rows: readonly EditRow[],
  orderedSkus: readonly string[]
): SkuGroup[] => {
  const by = new Map<string, SkuGroup>();
  orderedSkus.forEach((sku) => by.set(sku, { sku, items: [] }));
  rows.forEach((row, index) => {
    const g = by.get(row.sku) ?? { sku: row.sku, items: [] };
    g.items.push({ row, index });
    by.set(row.sku, g);
  });
  return Array.from(by.values()).filter((g) => g.items.length > 0);
};

const FIELD_LABEL: Record<RowField, string> = {
  sku: 'SKU',
  storageID: 'lokasi',
  stockID: 'lokasi',
  qty: 'qty',
};

/** Why a save is not allowed (the first thing to fix), null when it is. */
export const firstIssue = (
  check: EditCheck,
  rows: readonly EditRow[]
): string | null => {
  if (!rows.length) return 'Belum ada baris.';
  const inc = check.incomplete[0];
  if (inc !== undefined) {
    const fields = Array.from(
      new Set(check.rows[inc].missing.map((f) => FIELD_LABEL[f]))
    );
    return `Baris ${inc + 1} (${rows[inc].sku || 'SKU'}) belum lengkap: ${fields.join(', ')}.`;
  }
  const inv = check.invalid[0];
  if (inv !== undefined) {
    const msg = Object.values(check.rows[inv].errors)[0];
    return `Baris ${inv + 1} (${rows[inv].sku}): ${msg}`;
  }
  if (!check.totalsOk) return 'Total per SKU belum sesuai order.';
  return null;
};

/* -------------------------------- navigation ------------------------------ */

/** The next pending item after `from`, wrapping to the start; null = none. */
export const nextPendingIndex = (
  states: readonly ItemState[],
  from: number
): number | null => {
  for (let step = 1; step <= states.length; step += 1) {
    const i = (from + step) % states.length;
    if (i !== from && states[i] === 'pending') return i;
  }
  return null;
};

export const tally = (
  states: readonly ItemState[]
): Record<ItemState, number> => {
  const out: Record<ItemState, number> = {
    pending: 0,
    saved: 0,
    ready: 0,
    stale: 0,
    failed: 0,
  };
  states.forEach((s) => {
    out[s] += 1;
  });
  return out;
};

/**
 * A chip's short name: "pik/FORE/BDG/100726/0003" → "FORE/0003" (client +
 * sequence — two clients' pickings of one day never read the same); another
 * shape of code → its last four characters.
 */
export const chipLabel = (code: string): string => {
  const parts = code.split('/');
  return parts.length >= 5
    ? `${parts[1]}/${parts[parts.length - 1]}`
    : `…${code.slice(-4)}`;
};

export const MAX_PICKER_SUGGESTIONS = 6;

/** Distinct non-empty names, first seen first, at most `max`. */
export const suggestPickers = (
  names: readonly (string | null | undefined)[],
  max = MAX_PICKER_SUGGESTIONS
): string[] => {
  const seen = new Set<string>();
  names.forEach((n) => {
    const t = n?.trim();
    if (t) seen.add(t);
  });
  return Array.from(seen).slice(0, max);
};
